<div class="profile-page">
  <header class="page-head">
    <h1 class="profile-page__title">Your profile</h1>
    <p class="profile-page__subtitle">Manage your personal details and account security.</p>
  </header>

  @if (!loaded()) {
    <div class="profile-loading" aria-live="polite">
      <mat-spinner diameter="32" />
    </div>
  } @else {
    <!-- ── Personal details ───────────────────────────────────────────── -->
    <mat-card class="profile-card" appearance="outlined">
      <mat-card-content>
        <div class="card-heading">
          <app-icon name="user" [size]="20" />
          <h2 class="card-heading__title">Personal details</h2>
        </div>

        @if (profileError()) {
          <div class="inline-error" role="alert" aria-live="assertive">{{ profileError() }}</div>
        }
        @if (profileSaved()) {
          <div class="inline-success" role="status" aria-live="polite" data-testid="profile-saved">
            <app-icon name="check-circle" [size]="18" />
            Your profile has been updated.
          </div>
        }

        <form class="profile-form" [formGroup]="form" novalidate (ngSubmit)="saveProfile()">
          <!-- Email (read-only) -->
          <div class="field-group">
            <label class="field-label" for="profileEmail">Email address</label>
            <mat-form-field appearance="outline" class="full-width">
              <input matInput id="profileEmail" type="email" [value]="email()" readonly />
              <mat-hint>Contact support to change your email address.</mat-hint>
            </mat-form-field>
          </div>

          <!-- First name -->
          <div class="field-group">
            <label class="field-label" for="profileFirstName"
              >First name <span class="required-mark" aria-hidden="true">*</span></label
            >
            <mat-form-field appearance="outline" class="full-width">
              <input
                matInput
                id="profileFirstName"
                type="text"
                formControlName="firstName"
                autocomplete="given-name"
              />
              <mat-error>First name is required</mat-error>
            </mat-form-field>
          </div>

          <!-- Last name -->
          <div class="field-group">
            <label class="field-label" for="profileLastName"
              >Last name <span class="required-mark" aria-hidden="true">*</span></label
            >
            <mat-form-field appearance="outline" class="full-width">
              <input
                matInput
                id="profileLastName"
                type="text"
                formControlName="lastName"
                autocomplete="family-name"
              />
              <mat-error>Last name is required</mat-error>
            </mat-form-field>
          </div>

          <!-- Cell number -->
          <fieldset class="field-group field-group--fieldset">
            <legend class="field-label">Cell number</legend>
            <app-phone-input formControlName="cellNumber" />
          </fieldset>

          <!-- Job title -->
          <div class="field-group">
            <label class="field-label" for="profileJob">Job title</label>
            <mat-form-field appearance="outline" class="full-width">
              <input
                matInput
                id="profileJob"
                type="text"
                formControlName="jobPosition"
                autocomplete="organization-title"
              />
              <mat-hint>Optional</mat-hint>
            </mat-form-field>
          </div>

          <!-- Notification preferences -->
          <div class="toggle-row">
            <mat-slide-toggle formControlName="emailNotification">
              Email notifications
            </mat-slide-toggle>
            <mat-slide-toggle formControlName="smsNotification">SMS notifications</mat-slide-toggle>
          </div>

          <button
            mat-flat-button
            type="submit"
            class="save-btn"
            data-testid="profile-save"
            [disabled]="savingProfile() || form.invalid"
          >
            @if (savingProfile()) {
              <mat-spinner diameter="20" />
            } @else {
              Save changes
            }
          </button>
        </form>
      </mat-card-content>
    </mat-card>

    <!-- ── AI-assisted assessment consent (POPIA s72) ─────────────────── -->
    <mat-card class="profile-card" appearance="outlined">
      <mat-card-content>
        <div class="card-heading">
          <app-icon name="shield" [size]="20" />
          <h2 class="card-heading__title">AI-assisted assessment</h2>
          <!-- The sign-up tick's chip; with a business, each business's state shows below. -->
          @if (businessConsents().length === 0) {
            @if (aiConsentActive()) {
              <span class="status-chip status-chip--on" data-testid="ai-consent-status-on">
                <app-icon name="check" [size]="14" /> Consent given
              </span>
            } @else {
              <span class="status-chip status-chip--off" data-testid="ai-consent-status-off">
                No consent
              </span>
            }
          }
        </div>

        <p class="card-blurb">
          CapX uses AI providers, which may be outside South Africa and which CapX will ensure comply with POPIA, to check the documents submitted for a business and produce an advisory assessment for the CapX team. A person always makes the final decision. One consent, given for the business, covers every funding application made for it until the consent is withdrawn.
        </p>

        @if (aiConsentError()) {
          <div class="inline-error" role="alert" aria-live="assertive">{{ aiConsentError() }}</div>
        }

        <!--
          FLOW-297 — the consent is the BUSINESS's (§A6.7.2), so where this person acts for a
          business the card shows each business's consent, and the withdrawal is offered to
          everyone at it alike (UI-DA-60-A-02, -C-01). With no business yet, the sign-up tick
          below is what is carried to the business at its first application.
          PLACEHOLDER WORDING throughout (UI-DA-60-A-06): see app-ai-consent-withdraw.
        -->
        @for (b of businessConsents(); track b.entity_short_id) {
          <div
            class="business-consent"
            [attr.data-testid]="'business-consent-' + b.entity_short_id"
          >
            <h3 class="business-consent__name">{{ b.entity_name }}</h3>
            @if (b.ai_consent?.state === 'ACTIVE') {
              <p class="card-blurb" data-testid="business-consent-given">
                Consent given for the business on
                {{ b.ai_consent!.consented_at | date: 'medium' }}
                @if (b.ai_consent!.given_as === 'AGENT') {
                  , by the introducing agent
                }
                .
              </p>
            } @else {
              <app-ai-consent-checkbox
                [businessName]="b.entity_name"
                [testId]="'business-consent-checkbox-' + b.entity_short_id"
                [checked]="businessTicks().has(b.entity_short_id)"
                (changed)="tickBusiness(b.entity_short_id, $event)"
              />
              <button
                mat-flat-button
                type="button"
                class="save-btn"
                [attr.data-testid]="'business-consent-save-' + b.entity_short_id"
                [disabled]="aiConsentBusy() || !businessTicks().has(b.entity_short_id)"
                (click)="giveForBusiness(b.entity_short_id)"
              >
                Save consent
              </button>
            }
            <app-ai-consent-withdraw
              [state]="b.ai_consent"
              [confirm]="withdrawFor(b.entity_short_id)"
            />
          </div>
        } @empty {
          @if (aiConsentActive()) {
            <p class="card-blurb" data-testid="ai-consent-given-at">
              Consent given on {{ aiConsent()!.consented_at | date: 'medium' }}.
            </p>
            <app-ai-consent-withdraw
              [state]="aiConsent()"
              [confirm]="withdrawAiConsent"
              [message]="withdrawMessage"
            />
          } @else {
            @if (aiConsent()?.state === 'WITHDRAWN') {
              <p class="card-blurb" data-testid="ai-consent-withdrawn-at">
                You withdrew your consent on {{ aiConsent()!.revoked_at | date: 'medium' }}
                @if (aiConsent()!.recorded_by_ops) {
                  , recorded by CapX
                }
                . Uploads and AI assessment stay off for your applications until you consent again.
              </p>
            }
            <app-ai-consent-checkbox
              testId="profile-ai-consent-checkbox"
              [checked]="aiConsentTick()"
              (changed)="aiConsentTick.set($event)"
            />
            <button
              mat-flat-button
              type="button"
              class="save-btn"
              data-testid="profile-ai-consent-save"
              [disabled]="aiConsentBusy() || !aiConsentTick()"
              (click)="giveAiConsent()"
            >
              @if (aiConsentBusy()) {
                <mat-spinner diameter="20" />
              } @else {
                Save consent
              }
            </button>
          }
        }
      </mat-card-content>
    </mat-card>

    <!-- ── Security / Two-factor authentication ───────────────────────── -->
    <mat-card class="profile-card" appearance="outlined">
      <mat-card-content>
        <div class="card-heading">
          <app-icon name="lock" [size]="20" />
          <h2 class="card-heading__title">Two-factor authentication</h2>
          @if (twoFactorEnabled()) {
            <span class="status-chip status-chip--on" data-testid="twofa-status-on">
              <app-icon name="check" [size]="14" /> On
            </span>
          } @else {
            <span class="status-chip status-chip--off" data-testid="twofa-status-off">Off</span>
          }
        </div>

        <p class="card-blurb">
          Add a second step to your login using an authenticator app such as Google Authenticator,
          Authy, or 1Password.
        </p>

        @if (twoFactorError()) {
          <div class="inline-error" role="alert" aria-live="assertive">{{ twoFactorError() }}</div>
        }

        <!-- Enabled: offer to turn it off -->
        @if (twoFactorEnabled()) {
          <form class="twofa-disable" [formGroup]="disableForm" (ngSubmit)="disableTwoFactor()">
            <p class="card-blurb">
              To turn off two-factor authentication, enter a current code from your authenticator
              app.
            </p>
            <div class="field-group">
              <label class="field-label" for="disableCode">Authentication code</label>
              <mat-form-field appearance="outline" class="code-field">
                <input
                  matInput
                  id="disableCode"
                  type="text"
                  inputmode="numeric"
                  maxlength="6"
                  formControlName="code"
                  placeholder="123456"
                  data-testid="twofa-disable-code"
                />
                <mat-error>Enter the 6-digit code</mat-error>
              </mat-form-field>
            </div>
            <button
              mat-stroked-button
              type="submit"
              class="danger-btn"
              data-testid="twofa-disable-submit"
              [disabled]="twoFactorBusy() || disableForm.invalid"
            >
              @if (twoFactorBusy()) {
                <mat-spinner diameter="20" />
              } @else {
                Turn off two-factor
              }
            </button>
          </form>
        } @else if (twoFactorStep() === 'idle') {
          <!-- Disabled, idle: start setup -->
          <button
            mat-flat-button
            type="button"
            class="save-btn"
            data-testid="twofa-start"
            [disabled]="twoFactorBusy()"
            (click)="startTwoFactor()"
          >
            @if (twoFactorBusy()) {
              <mat-spinner diameter="20" />
            } @else {
              <app-icon name="smartphone" [size]="18" /> Set up two-factor
            }
          </button>
        } @else {
          <!-- Setup in progress: show QR + confirm -->
          <div class="twofa-setup">
            <ol class="twofa-steps">
              <li>Scan this QR code with your authenticator app.</li>
              <li>Enter the 6-digit code it shows to confirm.</li>
            </ol>

            <div class="twofa-qr">
              @if (qrDataUrl()) {
                <img
                  class="twofa-qr__img"
                  [src]="qrDataUrl()"
                  alt="QR code for two-factor authentication setup"
                  data-testid="twofa-qr"
                />
              }
              <div class="twofa-secret">
                <span class="twofa-secret__label">Can't scan? Enter this key manually:</span>
                <div class="twofa-secret__row">
                  <code class="twofa-secret__code" data-testid="twofa-secret">{{
                    twoFactorSecret()
                  }}</code>
                  <button
                    mat-icon-button
                    type="button"
                    class="twofa-secret__copy"
                    aria-label="Copy secret key"
                    (click)="copySecret()"
                  >
                    <app-icon name="copy" [size]="16" />
                  </button>
                </div>
                @if (secretCopied()) {
                  <span class="twofa-secret__copied" role="status">Copied</span>
                }
              </div>
            </div>

            <form class="twofa-confirm" [formGroup]="confirmForm" (ngSubmit)="confirmTwoFactor()">
              <div class="field-group">
                <label class="field-label" for="confirmCode">Authentication code</label>
                <mat-form-field appearance="outline" class="code-field">
                  <input
                    matInput
                    id="confirmCode"
                    type="text"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    maxlength="6"
                    formControlName="code"
                    placeholder="123456"
                    data-testid="twofa-confirm-code"
                  />
                  <mat-error>Enter the 6-digit code</mat-error>
                </mat-form-field>
              </div>
              <div class="twofa-confirm__actions">
                <button
                  mat-flat-button
                  type="submit"
                  data-testid="twofa-confirm-submit"
                  [disabled]="twoFactorBusy() || confirmForm.invalid"
                >
                  @if (twoFactorBusy()) {
                    <mat-spinner diameter="20" />
                  } @else {
                    Confirm and enable
                  }
                </button>
                <button mat-stroked-button type="button" (click)="cancelTwoFactor()">Cancel</button>
              </div>
            </form>
          </div>
        }
      </mat-card-content>
    </mat-card>
  }
</div>
